import React from "react";
import { Link } from "react-router-dom";
import Stars from "./Stars";
import { averageBlendedRating, ReviewLike } from "../utils/reviewScore";
import { API_URL as SERVER } from "../config";

export interface SaunaCardData {
  id: number;
  name: string;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  capacity: number;
  pricePerHourCents: number;
  saunaImagesBySaunaId?: { nodes: { url: string }[] };
  reviewsBySaunaId?: { totalCount: number; nodes: ReviewLike[] };
}

interface Props {
  sauna: SaunaCardData;
  variant?: "vertical" | "horizontal";
  highlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  date?: string | null;
}

function RatingBadge({ reviews }: { reviews?: SaunaCardData["reviewsBySaunaId"] }) {
  if (!reviews || reviews.totalCount === 0) return null;
  const avg = averageBlendedRating(reviews.nodes);
  if (avg === null) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 6 }}>
      <Stars rating={avg} />
      <span style={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>{avg.toFixed(1)}</span>
      <span style={{ fontSize: 12, color: "#6b7280" }}>({reviews.totalCount})</span>
    </div>
  );
}

export default function SaunaCard({ sauna: s, variant = "vertical", highlighted, onMouseEnter, onMouseLeave, date }: Props) {
  const thumb = s.saunaImagesBySaunaId?.nodes?.[0]?.url;
  const place = s.city ? [s.city, s.state || s.country].filter(Boolean).join(", ") : null;
  const price = `$${(s.pricePerHourCents / 100).toFixed(2)} / hr`;
  const detailPath = `/saunas/${s.id}${date ? `?date=${date}` : ""}`;

  if (variant === "horizontal") {
    return (
      <Link
        to={detailPath}
        style={{ textDecoration: "none", color: "inherit" }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div style={{ ...hCardStyle, outline: highlighted ? "2px solid #2563eb" : "none" }}>
          <div style={hImageWrapStyle}>
            {thumb ? (
              <img src={`${SERVER}${thumb}`} alt={s.name} style={imgStyle} />
            ) : (
              <div style={placeholderStyle}>No photo</div>
            )}
          </div>
          <div style={hBodyStyle}>
            <div style={nameStyle}>{s.name}</div>
            {place && <div style={metaStyle}>{place}</div>}
            <div style={metaStyle}>{s.capacity} {s.capacity === 1 ? "person" : "people"}</div>
            <RatingBadge reviews={s.reviewsBySaunaId} />
            <div style={{ marginTop: 6, fontSize: 14, color: "#111827" }}>
              <strong>{price}</strong>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={detailPath} style={{ textDecoration: "none", color: "inherit" }}>
      <div style={vCardStyle}>
        <div style={vImageWrapStyle}>
          {thumb ? (
            <img src={`${SERVER}${thumb}`} alt={s.name} style={imgStyle} />
          ) : (
            <div style={placeholderStyle}>No photo</div>
          )}
        </div>
        <div style={{ padding: "14px 4px 4px" }}>
          <div style={nameStyle}>{s.name}</div>
          {place && <div style={{ ...metaStyle, marginTop: 4 }}>{place}</div>}
          <div style={{ ...metaStyle, marginTop: 6 }}>{s.capacity} {s.capacity === 1 ? "person" : "people"}</div>
          <RatingBadge reviews={s.reviewsBySaunaId} />
          <div style={{ marginTop: 6, fontSize: 14, color: "#111827" }}>
            <strong>{price}</strong>
          </div>
        </div>
      </div>
    </Link>
  );
}

const imgStyle: React.CSSProperties = { width: "100%", height: "100%", objectFit: "cover", display: "block" };
const placeholderStyle: React.CSSProperties = {
  width: "100%", height: "100%", display: "flex", alignItems: "center",
  justifyContent: "center", color: "#9ca3af", fontSize: 13,
};
const nameStyle: React.CSSProperties = { fontSize: 15, fontWeight: 600, color: "#111827" };
const metaStyle: React.CSSProperties = { color: "#6b7280", fontSize: 13 };

const vCardStyle: React.CSSProperties = { background: "#fff", borderRadius: 12, overflow: "hidden", cursor: "pointer" };
const vImageWrapStyle: React.CSSProperties = {
  width: "100%", aspectRatio: "4 / 3", overflow: "hidden", borderRadius: 12, background: "#f3f4f6",
};

const hCardStyle: React.CSSProperties = {
  display: "flex", gap: 12, background: "#fff", borderRadius: 12,
  overflow: "hidden", cursor: "pointer", padding: 10,
  border: "1px solid #e5e7eb", transition: "box-shadow 0.15s",
};
const hImageWrapStyle: React.CSSProperties = {
  width: 120, flexShrink: 0, borderRadius: 8, overflow: "hidden",
  background: "#f3f4f6", aspectRatio: "4 / 3",
};
const hBodyStyle: React.CSSProperties = {
  flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0,
};
